// Mobile-only bottom tab bar. Hidden on md+ (desktop keeps the Sidebar).
// Tabs per role mirror the WolfDriver mobile app's bottom tabs:
//   Admin/Super Admin → Dashboard, Orders, Products, Stock, Chat
//   Dispatch          → Dashboard, Orders, Chat, Settings
//   Driver            → Driver, Chat, Settings
//   Store             → Store, Chat, Settings
//   Manager           → Home, Warehouse, Packaging, Products, Settings (no orders/chat)
//   Stock Manager     → Products, Stock, Settings (no orders/chat)
//
// The full sidebar remains reachable on mobile via the hamburger in the top
// header — bottom tabs cover the common flows; sidebar covers everything else.

import React from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../context/AppContext';
import { hasStoreAccess } from '../services/storeAccess';
import { useNotifications } from './NotificationHandler';
import { UserRole } from '../types';
import { FEATURES } from '../services/features';
import { LayoutDashboard, ClipboardList, Package, Boxes, MessageSquare, Truck, Settings as SettingsIcon, Store as StoreIcon, ArrowLeftRight, Shield, Factory, HardHat, SquareKanban } from 'lucide-react';

type Tab = {
  id: string;
  label: string;
  path: string;
  // match on the *prefix* — /orders/anything highlights the Orders tab
  matchPrefix: string;
  // Optional: also require `?tab=` query param to equal this value. Used by
  // the driver sub-tabs which all live under /driver_app.
  matchTab?: string;
  // When true, this sub-tab should be treated as active if the URL has no
  // `?tab=` param at all. Every group of sibling matchTab tabs needs exactly
  // one isDefaultTab — that's the one that's highlighted by default.
  isDefaultTab?: boolean;
  icon: React.ComponentType<{ size?: number; className?: string }>;
};

const tabsForRole = (role: UserRole): Tab[] => {
  switch (role) {
    case UserRole.OWNER:
    case UserRole.SUPER_ADMIN:
    case UserRole.ADMIN:
      return [
        { id: 'dashboard', label: 'Home', path: '/dashboard', matchPrefix: '/dashboard', icon: LayoutDashboard },
        { id: 'orders', label: 'Orders', path: '/orders', matchPrefix: '/orders', icon: ClipboardList },
        { id: 'products', label: 'Storage House', path: '/products', matchPrefix: '/products', icon: Package },
        { id: 'stock_overview', label: 'Stock', path: '/stock_overview', matchPrefix: '/stock_overview', icon: Boxes },
        { id: 'messages', label: 'Chat', path: '/messages', matchPrefix: '/messages', icon: MessageSquare },
      ];
    case UserRole.DISPATCH:
      return [
        { id: 'dashboard', label: 'Home', path: '/dashboard', matchPrefix: '/dashboard', icon: LayoutDashboard },
        { id: 'orders', label: 'Orders', path: '/orders', matchPrefix: '/orders', icon: ClipboardList },
        { id: 'messages', label: 'Chat', path: '/messages', matchPrefix: '/messages', icon: MessageSquare },
        { id: 'settings', label: 'Settings', path: '/settings', matchPrefix: '/settings', icon: SettingsIcon },
      ];
    case UserRole.DRIVER:
      // Driver gets the full 6-tab layout matching the mobile app: the
      // four DriverApp sub-tabs (deliveries/pickups/transfers/store) become
      // real bottom-tab entries driven by the ?tab= query param.
      return [
        { id: 'deliveries', label: 'Deliveries', path: '/driver_app', matchPrefix: '/driver_app', matchTab: 'deliveries', isDefaultTab: true, icon: Truck },
        { id: 'pickups', label: 'Pickups', path: '/driver_app?tab=pickups', matchPrefix: '/driver_app', matchTab: 'pickups', icon: Package },
        { id: 'transfers', label: 'Transfers', path: '/driver_app?tab=transfers', matchPrefix: '/driver_app', matchTab: 'transfers', icon: ArrowLeftRight },
        { id: 'store', label: 'Store', path: '/driver_app?tab=store', matchPrefix: '/driver_app', matchTab: 'store', icon: Shield },
        // No Tasks tab for a driver (client, 2026-10-12): drivers do not see or manage tasks.
        { id: 'messages', label: 'Chat', path: '/messages', matchPrefix: '/messages', icon: MessageSquare },
        { id: 'settings', label: 'Settings', path: '/settings', matchPrefix: '/settings', icon: SettingsIcon },
      ];
    case UserRole.STORE:
      // Store gets the same 4-tab split the mobile app has: Orders (active
      // prep queue), Stock (loadouts, returns, give-stock-to-driver), Chat,
      // Settings. ?tab= query param drives StoreHub's internal split.
      return [
        { id: 'orders', label: 'Orders', path: '/store_hub', matchPrefix: '/store_hub', matchTab: 'orders', isDefaultTab: true, icon: Package },
        { id: 'stock', label: 'Stock', path: '/store_hub?tab=stock', matchPrefix: '/store_hub', matchTab: 'stock', icon: Boxes },
        { id: 'messages', label: 'Chat', path: '/messages', matchPrefix: '/messages', icon: MessageSquare },
        { id: 'settings', label: 'Settings', path: '/settings', matchPrefix: '/settings', icon: SettingsIcon },
      ];
    // Warehouse-pipeline roles get NO Chat tab (removed 2026-08-28 per the owner).
    // Worker and legacy packager users both land on the merged Packaging House
    // hub; the stored role id stays 'worker'.
    case UserRole.WAREHOUSE_MANAGER:
      return [
        { id: 'warehouse_manager', label: 'Warehouse', path: '/warehouse_manager', matchPrefix: '/warehouse_manager', icon: Factory },
        { id: 'settings', label: 'Settings', path: '/settings', matchPrefix: '/settings', icon: SettingsIcon },
      ];
    case UserRole.WORKER:
    case UserRole.PACKAGER:
      return [
        { id: 'worker', label: 'Packaging', path: '/worker', matchPrefix: '/worker', icon: HardHat },
        { id: 'settings', label: 'Settings', path: '/settings', matchPrefix: '/settings', icon: SettingsIcon },
      ];
    case UserRole.MANAGER:
      return [
        { id: 'dashboard', label: 'Home', path: '/dashboard', matchPrefix: '/dashboard', icon: LayoutDashboard },
        { id: 'warehouse_manager', label: 'Warehouse', path: '/warehouse_manager', matchPrefix: '/warehouse_manager', icon: Factory },
        { id: 'worker', label: 'Packaging', path: '/worker', matchPrefix: '/worker', icon: HardHat },
        { id: 'products', label: 'Storage House', path: '/products', matchPrefix: '/products', icon: Package },
        { id: 'settings', label: 'Settings', path: '/settings', matchPrefix: '/settings', icon: SettingsIcon },
      ];
    case UserRole.STOCK_MANAGER:
      return [
        { id: 'products', label: 'Storage House', path: '/products', matchPrefix: '/products', icon: Package },
        { id: 'stock_overview', label: 'Stock', path: '/stock_overview', matchPrefix: '/stock_overview', icon: Boxes },
        { id: 'settings', label: 'Settings', path: '/settings', matchPrefix: '/settings', icon: SettingsIcon },
      ];
    case UserRole.TEAM_MEMBER:
      return [
        { id: 'tasks', label: 'Tasks', path: '/tasks', matchPrefix: '/tasks', icon: SquareKanban },
        { id: 'settings', label: 'Settings', path: '/settings', matchPrefix: '/settings', icon: SettingsIcon },
      ];
    default:
      return [];
  }
};

export const MobileBottomTabs: React.FC = () => {
  const currentUser = useAppSelector(s => s.currentUser);
  const { unreadCount, unreadByChannel } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  if (!currentUser) return null;

  // A driver without store access (0084) has no Store tab.
  const tabs = tabsForRole(currentUser.role).filter(t => t.id !== 'store' || currentUser.role !== UserRole.DRIVER || hasStoreAccess(currentUser));
  if (tabs.length === 0) return null;

  return (
    // Flex child (not fixed) so it lives inside the bounded 100dvh app shell.
    // When the mobile keyboard opens, 100dvh shrinks, the flex container
    // shrinks, and this tab bar stays pinned above the keyboard — WhatsApp
    // behavior. Fixed positioning would fight with virtual keyboards on iOS.
    <nav
      role="navigation"
      aria-label="Mobile navigation"
      className="md:hidden shrink-0 z-30 bg-white border-t border-slate-200 flex pb-[env(safe-area-inset-bottom)]"
    >
      {tabs.map(t => {
        const Icon = t.icon;
        const pathMatches = location.pathname === t.matchPrefix || location.pathname.startsWith(t.matchPrefix + '/');
        let isActive: boolean;
        if (t.matchTab) {
          // Sub-tab under a path — requires both path AND ?tab= to match.
          // Missing ?tab= means the default tab is active (isDefaultTab=true).
          const queryTab = searchParams.get('tab');
          isActive = pathMatches && (queryTab === t.matchTab || (!queryTab && !!t.isDefaultTab));
        } else {
          // Sibling sub-tab entries (different matchTab) live under the same
          // path — only highlight primary tab if no sibling is present.
          const hasSibling = tabs.some(other => other !== t && other.matchPrefix === t.matchPrefix && other.matchTab);
          isActive = pathMatches && !hasSibling;
        }
        const badge = t.id === 'messages' ? unreadCount : 0;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => navigate(t.path)}
            aria-current={isActive ? 'page' : undefined}
            className={`flex-1 flex flex-col items-center justify-center py-2 transition-colors ${
              isActive ? 'text-brand-600' : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            <div className="relative">
              <Icon size={22} className={isActive ? 'text-brand-600' : 'text-slate-400'} />
              {badge > 0 && (
                <span className="absolute -top-1 -right-2 bg-red-500 text-white text-[10px] font-black min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">
                  {badge > 9 ? '9+' : badge}
                </span>
              )}
              {t.id === 'messages' && unreadByChannel.customer > 0 && (
                <span
                  title={`${unreadByChannel.customer} customer SMS reply${unreadByChannel.customer === 1 ? '' : 'ies'}`}
                  className="absolute -bottom-1 -right-2 bg-green-500 text-white text-[8px] font-black min-w-[14px] h-[14px] rounded-full flex items-center justify-center px-1"
                >
                  SMS
                </span>
              )}
            </div>
            <span className={`text-[10px] font-semibold mt-0.5 ${isActive ? 'text-brand-700' : 'text-slate-500'}`}>
              {t.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
};
